import React from 'react';
import {Menu, MenuItem, SxProps, Theme} from '@mui/material';
import {useIntl} from 'react-intl';
import {SVGS} from '../../../assets/images';
import {Lx} from '../../../i18n/consts';

export interface RowActionsMenuPosition {
    top: number;
    left: number;
}

interface TimeTrackingImportRowActionsMenuProps {
    position: RowActionsMenuPosition | null;
    onEdit: () => void;
    onRemove: () => void;
    onClose: () => void;
}

/**
 * The menu goes into a portal, so the .pageScope variables do not reach it — only shared theme
 * tokens are used here. The styles are set through sx rather than module classes: otherwise they
 * are overridden both by the MUI palette (which has no mode set, so it is always light) and by
 * the global pico, which puts list markers, padding and its own color on ul/li.
 */
const paperSx: SxProps<Theme> = {
    minWidth: '200px',
    borderRadius: '8px',
    border: '1px solid var(--border-primary)',
    backgroundColor: 'var(--background-secondary-not-transparent)',
    backgroundImage: 'none',
    color: 'var(--text-primary)',
    boxShadow: '0 8px 24px var(--popover-shadow-color)',
};

const listSx: SxProps<Theme> = {
    margin: 0,
    padding: '4px',
    listStyle: 'none',
    // The app theme hardcodes a dark background on MuiList (#121212), so it is cleared to let the
    // Paper background through — otherwise the menu stays black in the light theme.
    backgroundColor: 'transparent',

    '& li': {
        listStyle: 'none',
    },
};

const itemSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '36px',
    padding: '8px 12px',
    borderRadius: '6px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 500,

    '& svg': {
        flex: '0 0 auto',
        width: '16px',
        height: '16px',
    },

    '& svg path': {
        fill: 'var(--icons-secondary)',
    },

    '&:hover': {
        backgroundColor: 'var(--background-secondary)',
        color: 'var(--text-primary)',

        '& svg path': {
            fill: 'var(--icons-primary)',
        },
    },
};

/**
 * Actions menu of a preview row. Anchored to the click point rather than to a button: it opens by
 * clicking the row itself.
 */
export default function TimeTrackingImportRowActionsMenu({
    position,
    onEdit,
    onRemove,
    onClose,
}: TimeTrackingImportRowActionsMenuProps) {
    const intl = useIntl();

    return (
        <Menu
            open={Boolean(position)}
            onClose={onClose}
            anchorReference='anchorPosition'
            anchorPosition={position ?? undefined}
            PaperProps={{sx: paperSx}}
            MenuListProps={{sx: listSx, dense: true}}
        >
            <MenuItem
                sx={itemSx}
                onClick={() => {
                    onClose();
                    onEdit();
                }}
            >
                <SVGS.EditIcon />
                <span>{intl.formatMessage({id: Lx.TimeTrackingImport.MANUAL_ROW_EDIT})}</span>
            </MenuItem>
            <MenuItem
                sx={itemSx}
                onClick={() => {
                    onClose();
                    onRemove();
                }}
            >
                <SVGS.DeleteIcon />
                <span>{intl.formatMessage({id: Lx.TimeTrackingImport.MANUAL_ROW_REMOVE})}</span>
            </MenuItem>
        </Menu>
    );
}
